/**
 * Outlook Calendar Sync Page
 * Manage Outlook calendar integration, connection status, and synced events
 */

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Calendar,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Unplug,
  Plus,
  Info,
  Key,
  Shield,
} from 'lucide-react';

interface SyncedEvent {
  id: string;
  entityType: string;
  entityId: string;
  outlookEventId: string;
  lastSynced: string;
  syncDirection: string;
}

export function OutlookSyncPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [isSyncDialogOpen, setIsSyncDialogOpen] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [syncFormData, setSyncFormData] = useState({
    entityType: 'deal',
    entityId: '',
  });

  // Check URL params for auth callback status on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('status');
    if (status === 'success') {
      setStatusMessage({ type: 'success', text: 'Successfully connected to Outlook!' });
      queryClient.invalidateQueries({ queryKey: ['outlookStatus'] });
      // Clean up URL params
      window.history.replaceState({}, '', window.location.pathname);
    } else if (status === 'error') {
      const errorMsg = params.get('message') || 'Failed to connect to Outlook. Please try again.';
      setStatusMessage({ type: 'error', text: errorMsg });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [queryClient]);

  // Fetch connection status
  const { data: statusData, isLoading: statusLoading } = useQuery({
    queryKey: ['outlookStatus'],
    queryFn: () => api.getOutlookStatus(),
  });
  const connected = statusData?.data?.connected ?? false;
  const expiresAt = statusData?.data?.expiresAt;

  // Fetch synced events
  const { data: eventsData } = useQuery({
    queryKey: ['syncedEvents', page],
    queryFn: () => api.getSyncedEvents({ page, limit: 20 }),
    enabled: connected,
  });
  const events = (eventsData?.data || []) as SyncedEvent[];
  const pagination = eventsData?.pagination;

  // Connect mutation
  const connectMutation = useMutation({
    mutationFn: () => api.getOutlookAuthUrl(),
    onSuccess: (response) => {
      const authUrl = response.data?.authUrl;
      if (authUrl) {
        window.location.href = authUrl;
      }
    },
    onError: () => {
      setStatusMessage({ type: 'error', text: 'Failed to get authorization URL. Please try again.' });
    },
  });

  // Disconnect mutation
  const disconnectMutation = useMutation({
    mutationFn: () => api.disconnectOutlook(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['outlookStatus'] });
      queryClient.invalidateQueries({ queryKey: ['syncedEvents'] });
      setStatusMessage({ type: 'success', text: 'Disconnected from Outlook.' });
    },
  });

  // Sync event mutation
  const syncEventMutation = useMutation({
    mutationFn: (data: { entityType: string; entityId: string }) =>
      api.syncEventToOutlook(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['syncedEvents'] });
      setIsSyncDialogOpen(false);
      setSyncFormData({ entityType: 'deal', entityId: '' });
      setStatusMessage({ type: 'success', text: 'Event synced to Outlook successfully.' });
    },
    onError: () => {
      setStatusMessage({ type: 'error', text: 'Failed to sync event. Please try again.' });
    },
  });

  function handleSyncSubmit() {
    if (!syncFormData.entityId.trim()) return;
    syncEventMutation.mutate(syncFormData);
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Outlook Calendar Sync</h1>
        <p className="text-muted-foreground">
          Connect and sync your calendar events with Microsoft Outlook
        </p>
      </div>

      {/* Status Message */}
      {statusMessage && (
        <div
          className={`p-4 rounded-lg border flex items-center justify-between ${
            statusMessage.type === 'success'
              ? 'bg-green-50 border-green-200 text-green-800 dark:bg-green-950 dark:border-green-800 dark:text-green-200'
              : 'bg-red-50 border-red-200 text-red-800 dark:bg-red-950 dark:border-red-800 dark:text-red-200'
          }`}
        >
          <div className="flex items-center gap-2">
            {statusMessage.type === 'success' ? (
              <CheckCircle2 className="h-4 w-4" />
            ) : (
              <XCircle className="h-4 w-4" />
            )}
            <span className="text-sm">{statusMessage.text}</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setStatusMessage(null)}
            className="h-6 px-2 text-xs"
          >
            Dismiss
          </Button>
        </div>
      )}

      {/* Connection Status Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Connection Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          {statusLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <RefreshCw className="h-4 w-4 animate-spin" />
              Checking connection status...
            </div>
          ) : connected ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="h-3 w-3 rounded-full bg-green-500" />
                <Badge variant="secondary" className="text-green-700 dark:text-green-300">
                  Connected
                </Badge>
              </div>
              {expiresAt && (
                <p className="text-sm text-muted-foreground">
                  Token expires: {new Date(expiresAt).toLocaleString()}
                </p>
              )}
              <Button
                variant="destructive"
                onClick={() => {
                  if (confirm('Are you sure you want to disconnect from Outlook?')) {
                    disconnectMutation.mutate();
                  }
                }}
                disabled={disconnectMutation.isPending}
              >
                <Unplug className="h-4 w-4 mr-2" />
                {disconnectMutation.isPending ? 'Disconnecting...' : 'Disconnect'}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="h-3 w-3 rounded-full bg-red-500" />
                <Badge variant="secondary" className="text-red-700 dark:text-red-300">
                  Not Connected
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                Connect your Microsoft Outlook account to sync calendar events.
              </p>
              <Button
                onClick={() => connectMutation.mutate()}
                disabled={connectMutation.isPending}
              >
                <Calendar className="h-4 w-4 mr-2" />
                {connectMutation.isPending ? 'Redirecting...' : 'Connect to Outlook'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Synced Events Section */}
      {connected && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <RefreshCw className="h-5 w-5" />
                Synced Events
              </CardTitle>
              <Button onClick={() => setIsSyncDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Sync Event
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Entity Type</TableHead>
                  <TableHead>Entity ID</TableHead>
                  <TableHead>Outlook Event ID</TableHead>
                  <TableHead>Last Synced</TableHead>
                  <TableHead>Sync Direction</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {events.map((event) => (
                  <TableRow key={event.id}>
                    <TableCell>
                      <Badge variant="outline">{event.entityType}</Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{event.entityId}</TableCell>
                    <TableCell className="font-mono text-xs max-w-[200px] truncate">
                      {event.outlookEventId}
                    </TableCell>
                    <TableCell>{new Date(event.lastSynced).toLocaleString()}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{event.syncDirection}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
                {events.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                      No synced events yet. Click "Sync Event" to get started.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-muted-foreground">
                  Showing {(page - 1) * 20 + 1}-{Math.min(page * 20, pagination.total)} of{' '}
                  {pagination.total}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage(page - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= pagination.totalPages}
                    onClick={() => setPage(page + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Info Cards */}
      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <Key className="h-4 w-4" />
              Required Environment Variables
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {[
                'MICROSOFT_CLIENT_ID',
                'MICROSOFT_CLIENT_SECRET',
                'MICROSOFT_TENANT_ID',
                'MICROSOFT_REDIRECT_URI',
              ].map((envVar) => (
                <div key={envVar} className="flex items-center gap-2">
                  <Info className="h-3 w-3 text-muted-foreground" />
                  <code className="text-xs bg-muted px-2 py-1 rounded font-mono">{envVar}</code>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <Shield className="h-4 w-4" />
              Required Scopes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {[
                { scope: 'Calendars.ReadWrite', desc: 'Read and write calendar events' },
                { scope: 'offline_access', desc: 'Maintain access with refresh tokens' },
                { scope: 'User.Read', desc: 'Read user profile information' },
              ].map((item) => (
                <div key={item.scope} className="flex items-start gap-2">
                  <Badge variant="outline" className="text-xs shrink-0 mt-0.5">
                    {item.scope}
                  </Badge>
                  <span className="text-xs text-muted-foreground">{item.desc}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sync Event Dialog */}
      <Dialog open={isSyncDialogOpen} onOpenChange={setIsSyncDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Sync Event to Outlook</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Entity Type</Label>
              <Select
                value={syncFormData.entityType}
                onValueChange={(v) => setSyncFormData({ ...syncFormData, entityType: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="deal">Deal</SelectItem>
                  <SelectItem value="activity">Activity</SelectItem>
                  <SelectItem value="ticket">Ticket</SelectItem>
                  <SelectItem value="contract">Contract</SelectItem>
                  <SelectItem value="invoice">Invoice</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Entity ID</Label>
              <Input
                value={syncFormData.entityId}
                onChange={(e) => setSyncFormData({ ...syncFormData, entityId: e.target.value })}
                placeholder="Enter the entity ID"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsSyncDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSyncSubmit}
              disabled={!syncFormData.entityId.trim() || syncEventMutation.isPending}
            >
              {syncEventMutation.isPending ? 'Syncing...' : 'Sync Event'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
